import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { SERVER_URL } from "../lib/constants";
import logo from "../../assets/cmxlogo-removebg-preview.png";
import UserService from "../../service/UserService";

const OauthLogin = () => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [dots, setDots] = useState("");

  const isCallmaxEmail = (value) => {
    const trimmed = (value || "").trim().toLowerCase();
    return trimmed.endsWith("@callmaxsolutions.com");
  };

  const handleManualOtpLogin = async () => {
    setError("");

    if (!email) {
      setError("Please enter your email address.");
      return;
    }

    setIsSending(true);

    try {
      const checkRes = await fetch(`${SERVER_URL}/api/auth/check-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const checkData = await checkRes.json();

      if (checkRes.status === 403) {
        setError(checkData.error || "Use Google login for this email.");
        return;
      }

      if (!checkRes.ok || !checkData.success) {
        setError(checkData.error || "Email is not registered.");
        return;
      }

      // Store pending (not authenticated yet)
      const { firstname, lastname, middlename } = checkData.user || {};

      UserService.setPendingApplicant({
        email,
        firstname: firstname || "",
        lastname: lastname || "",
        middlename: middlename || "",
        picture: "/default-avatar.png",
      });

      const otpRes = await fetch(`${SERVER_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emailAddress: email,
        }),
      });
      if (!otpRes) return;

      if (otpRes.status === 429) {
        const result = await otpRes.json();
        setError(result.message || "Too many requests. Please wait.");
        return;
      }

      const result = await otpRes.json();

      if (!otpRes.ok || !result.success) {
        setError("Failed to send OTP. Please try again.");
        return;
      }
      // ===============================
      // ✅ STORE SECURE DATA
      // ===============================
      localStorage.setItem("pendingChallengeId", result.challengeId);
      localStorage.setItem("pendingEmail", email);
      localStorage.setItem("pendingExpiryAt", result.expiresAt);
      localStorage.setItem("otpCooldownStart", Date.now());

      navigate("/OTP-SECURE", {
        state: {
          emailAddress: email,
        },
      });
    } catch (err) {
      console.error("OTP login error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);

      const generatedAppId = `google_${decoded.sub}`;

      // Save for potential use later
      localStorage.setItem("applicationId", generatedAppId);
      localStorage.setItem("applicantName", decoded.name || "");
      localStorage.setItem("applicantPicture", decoded.picture || "");
      localStorage.setItem("applicantEmail", decoded.email || "");
      localStorage.setItem("applicantFirstname", decoded.given_name || "");
      localStorage.setItem("applicantLastname", decoded.family_name || "");
      localStorage.setItem("applicantMiddlename", "");

      // 👉 Only check if registered
      const res = await fetch(`${SERVER_URL}/auth/oauth-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: "google",
          email: decoded.email,
          provider_id: decoded.sub,
        }),
      });

      const result = await res.json();

      if (res.status === 200 && result.success) {
        // Existing user — authenticate
        UserService.loginApplicant({
          email: decoded.email,
          firstname: decoded.given_name || "",
          lastname: decoded.family_name || "",
          picture: decoded.picture || "/default-avatar.png",
          method: "google",
          providerId: decoded.sub,
        });
        // ✅ Existing user — go to Applications
        navigate("/Applications", { state: { appId: generatedAppId } });
      } else if (res.status === 202) {
        // ✅ Needs registration
        navigate("/Register", {
          state: {
            provider: "google",
            email: decoded.email,
            firstname: decoded.given_name,
            lastname: decoded.family_name,
            provider_id: decoded.sub,
            picture: decoded.picture,
          },
        });
      } else if (res.status === 409) {
        setError(result.error || "Email is already registered manually.");
      } else {
        setError("Unexpected error during Google login.");
      }
    } catch (err) {
      console.error("OAuth login error:", err);
      setError("Login failed. Please try again.");
    }
  };

  useEffect(() => {
    if (!isSending) {
      setDots("");
      return;
    }

    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev.length >= 3) return "";
        return prev + ".";
      });
    }, 400);

    return () => clearInterval(interval);
  }, [isSending]);

  return (
    <div className="flex justify-center items-center min-h-screen bg-linear-gradient px-4">
      <div className="bg-white p-8 md:p-10 rounded-lg shadow-xl w-full max-w-lg text-center">
        <img src={logo} alt="Callmax Logo" className="w-24 mx-auto mb-6" />
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Quick Apply
        </h2>

        {/* Manual Email OTP Login */}
        <div className="mb-6 text-left">
          <label className="text-sm text-gray-700 mb-1 block">Email</label>
          <input
            type="email"
            placeholder="Enter your registered email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border rounded px-3 py-2 mb-3"
          />

          <button
            onClick={handleManualOtpLogin}
            disabled={isSending}
            className="w-full bg-[#162950] hover:bg-[#1c365f] text-white py-2 rounded"
          >
            {isSending ? "Sending OTP..." : "Login with OTP"}
          </button>
        </div>

        {/* Register Link */}
        <div className="text-sm text-center mt-4">
          Don’t have an account?{" "}
          <span
            className="text-blue-600 hover:underline cursor-pointer"
            onClick={() => navigate("/Register")}
          >
            Register here
          </span>
        </div>

        {/* Google Login */}
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-white px-2 text-gray-500">or sign in with</span>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <GoogleLogin
            onSuccess={handleGoogleLoginSuccess}
            onError={() => setError("Google login failed")}
          />
        </div>

        {error && <p className="text-red-600 text-sm mt-4">{error}</p>}
      </div>
    </div>
  );
};

export default OauthLogin;
